import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

interface ProductCard {
  title: string
  description: string
  icon: string
  href: string
  badge?: string
  external?: boolean
}

export default function ProductsPage() {
  const { t } = useTranslation('products')

  const productCards: ProductCard[] = [
    {
      title: t('synstreamEnterprise.title', 'Synstream Enterprise'),
      description: t('synstreamEnterprise.description', 'Connect your existing systems and turn their data into action'),
      icon: '/icon.png',
      href: 'https://dashboard.nonprod.rtsinv.com/',
      external: true
    },
    {
      title: t('pipelineStudio.title', 'Pipeline Studio'),
      description: t('pipelineStudio.description', 'Manage and monitor your data pipelines'),
      icon: '/PS.png',
      href: '/products/pipeline-studio'
    },
    {
      title: t('autoMonitor.title', 'Auto Monitor'),
      description: t('autoMonitor.description', 'Comprehensive monitoring and analytics platform'),
      icon: '/AM.png',
      href: 'https://apac.eginnovations.com/',
      external: true
    },
    {
      title: t('totalCompliance.title', 'Total Compliance One Integrated Workflow'),
      description: t('totalCompliance.description', 'End-to-end compliance, screening and onboarding in a single workflow'),
      icon: '/TC.svg',
      href: 'https://athena3-demo-uat.cynopsis.co/athena',
      external: true
    }
  ]

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{t('title', 'Products')}</h1>
        <p className="text-gray-600 mt-2">{t('description', 'Explore and manage your SynStream products')}</p>
      </div>

      {/* Products Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {productCards.map((card) => {
          const CardContent = (
            <div className="flex items-start space-x-4">
              <div className="shrink-0 w-12 h-12 bg-white rounded-lg flex items-center justify-center overflow-hidden">
                <img src={card.icon} alt={card.title} className="w-full h-full object-contain" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary transition-colors duration-200">
                    {card.title}
                  </h3>
                  {card.badge && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                      {card.badge}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600">
                  {card.description}
                </p>
              </div>
              <div className="shrink-0">
                <svg
                  className="w-5 h-5 text-gray-400 group-hover:text-primary transition-colors duration-200"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          )

          return card.external ? (
            <a
              key={card.href}
              href={card.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white rounded-xl shadow-lg border border-gray-200 p-6 transition-all duration-200 hover:shadow-xl hover:border-primary hover:-translate-y-1"
            >
              {CardContent}
            </a>
          ) : (
            <Link
              key={card.href}
              to={card.href}
              className="group bg-white rounded-xl shadow-lg border border-gray-200 p-6 transition-all duration-200 hover:shadow-xl hover:border-primary hover:-translate-y-1"
            >
              {CardContent}
            </Link>
          )
        })}
      </div>
    </div>
  )
}
